import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FirEpBumpService } from './fir-ep-bump.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir-ep-bump',
    templateUrl: './fir-ep-bump.component.html',
    styleUrls: ['./fir-ep-bump.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FirEpBumpComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};
  modalName: string = '';
  private subscriptions = new Subscription();
  private baseGlobalParams: any = {};

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to update the Indicator for selected Entities?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Component properties following fir163jsync pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  showModal: boolean = false;
  selectedData: any[] = [];
  logged_in_user: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private firEpBumpService: FirEpBumpService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subscriptions.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = this.baseURLs;
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(this.baseGlobalParams);
    }
    this.filterParams = fp;
    // Prefer the full batch array preserved on activeScreen.data.gridData by
    // CeHostDialog. prepareDialogData (grid.directive) unwraps only the first
    // batch entry into activeScreen.data.rowData, so reading rowData alone
    // would silently drop every other selected row when the user triggers
    // the YES/NO batch action from the grid toolbar.
    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    const rawRow = Array.isArray(gridPayload) && gridPayload.length > 0
      ? gridPayload
      : rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(rawRow)) {
      const o: any = {};
      rawRow.forEach((r: any, i: number) => {
        o[i] = r?.data !== undefined ? r : { data: r };
      });
      this.rowData = o;
    } else if (rawRow && typeof rawRow === 'object' && Object.keys(rawRow).length > 0) {
      // Distinguish multi-row map (values are row objects) from a single row
      // (values are primitives/null because keys are column names). When grid
      // emits a single selected row, wrap it so processGridData iterates rows,
      // not column names.
      const values = Object.values(rawRow);
      const hasObjectValues = values.some(
        (v: any) => v !== null && typeof v === 'object'
      );
      if (hasObjectValues) {
        this.rowData = rawRow;
      } else {
        this.rowData = { 0: { data: rawRow } };
      }
    }
    if (this.gtwApp?.USER_SETTINGS?.user?.sso_id) {
      this.USER_SETTINGS = this.gtwApp.USER_SETTINGS;
      this.logged_in_user = String(this.gtwApp.USER_SETTINGS.user.sso_id);
    }
    if (this.gtwApp?.SERVER_CONFIG) {
      this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG;
    }
    this.globalService = {
      globalParams: this.baseGlobalParams,
      modalScreen: this.baseGlobalParams?.modalScreen,
      ...this.baseGlobalParams
    };
    if (this.baseGlobalParams?.modalScreen?.modal_name) {
      this.modalName = this.baseGlobalParams.modalScreen.modal_name;
    }
    // CeHostDialog.bridgeDataToActiveScreen writes the modal name onto
    // activeScreen.data; hydrateGlobalParamsFromContext does NOT propagate
    // modalScreen into baseGlobalParams, so reading from globalService alone
    // leaves modalName empty and processGridData falls back to COL_VALUE = 1
    // (YES) regardless of which batch action was triggered.
    const activeData = activeScreen?.data || {};
    const activeModalName = activeData.modal_name || activeData.modalName;
    if (activeModalName) {
      this.modalName = activeModalName;
    }
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 &&
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  private tryInit(): void {
    this.init();
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS like fir163jsync
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
    
    // Get modal name from globalService or filterParams
    if (this.globalService?.modalScreen?.modal_name) {
      this.modalName = this.globalService.modalScreen.modal_name;
    }
    // Prefer activeScreen.data (set by CeHostDialog) so YES vs NO is
    // distinguished correctly when both modals share this custom element.
    const activeData = this.gtwWorkspace.workspaceFactory?.activeScreen?.data || {};
    const activeModalName = activeData.modal_name || activeData.modalName;
    if (activeModalName) {
      this.modalName = activeModalName;
    }
    
    // Process grid data if rowData is available
    if (this.rowData) {
      this.processGridData();
    }
  }

  processGridData(): void {
    // Process grid data exactly like AngularJS controller
    if (this.rowData != null) {
      this.selectedData = [];
      
      // Clone the grid data like AngularJS
      const tempGridData = _.clone(this.rowData);
      const gridDataMultipleSelect = _.clone(tempGridData);
      
      if (gridDataMultipleSelect != undefined) {
        for (var key in gridDataMultipleSelect) {
          const gridObj: any = {};

          // Access data property like AngularJS: vm.gridDataMultipleSelect[key].data
          const dataItem = gridDataMultipleSelect[key]?.data || gridDataMultipleSelect[key];

          // Skip iterations where dataItem is not a row object (would happen if
          // rowData accidentally was a single row whose column values are primitives)
          if (dataItem == null || typeof dataItem !== 'object') {
            continue;
          }

          // Skip rows without the required key — nothing meaningful to send
          if (dataItem.COMBINATION_KEY == null) {
            continue;
          }

          // Extract relevant fields based on AngularJS controller
          gridObj["TIER_1_CFC_COMBINATION_KEY"] = dataItem.COMBINATION_KEY;
          gridObj["US_SH_COMBINATION_KEY"] = dataItem.US_SH_COMBINATION_KEY;
          gridObj["COL_VALUE"] = this.modalName === 'epBumpIndUpdateNo' ? 0 : 1;

          this.selectedData.push(gridObj);
        }
      }
    }
  }

  onConfirm(): void {
    this.confirmSave();
  }

  cancel(): void {
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    this.dispatchGridUpdate('cancel');
  }

  /**
   * Close the dialog after a successful save and ask the host grid to
   * reload. CeHostDialog's `request-grid-update` listener treats any detail
   * other than 'cancel' as a refresh signal, which causes grid.directive to
   * call loadGridData(activeScreen, true) in dialogRef.afterClosed().
   */
  private closeWithRefresh(): void {
    this.dispatchGridUpdate('refresh');
  }

  private dispatchGridUpdate(detail: 'cancel' | 'refresh'): void {
    this.showModal = false;
    this.requestGridUpdate.emit(detail);
  }

  onCancel(): void {
    // Called from confirmation modal - delegates to cancel()
    this.cancel();
  }

  confirmSave(): void {
    // Follow exact AngularJS behavior
    const returnClobSettingsObj: any = {};
    
    // Build settings object with real data (NOT hardcoded)
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    
    const message = "First tier CFC 965 Deficit indicator has been changed.";
    
    // Call sendDetails function like AngularJS
    this.sendDetails(returnClobSettingsObj, this.selectedData, message);
  }

  sendDetails(returnClobSettingsObj: any, saveDtls: any[], message: string): void {
    // Show loader in the modal - update modalConfig to trigger change detection
    this.modalConfig = { ...this.modalConfig, loading: true };
    
    // Call service method like AngularJS
    const apiBase = this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api || '';
    const sub = this.firEpBumpService.updateIndicator(
      apiBase,
      saveDtls,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.logged_in_user,
    ).subscribe(
      (result: any) => {
        // Hide loader - update modalConfig to trigger change detection
        this.modalConfig = { ...this.modalConfig, loading: false };
        
        // Handle response exactly like AngularJS
        if (result.errorMessage && result.errorMessage !== 'null') {
          // Show error message but keep modal open
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show success message
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');

          // Keep the Angular @Output for any direct consumers (legacy AngularJS
          // bridge), and dispatch the CustomEvent CeHostDialog listens for so
          // grid.directive's afterClosed handler triggers loadGridData and the
          // grid reflects the new YES/NO status without a manual refresh.
          this.requestGridUpdate.emit('refresh');
          this.closeWithRefresh();
        }
      },
      (error: any) => {
        // Hide loader on error - update modalConfig to trigger change detection
        this.modalConfig = { ...this.modalConfig, loading: false };
        
        console.error('FIR EP Bump update error:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while updating indicator. Please try again.', 'gtw-snackbar--red');
      }
    );
    this.subscriptions.add(sub);
  }
}